import React, { useEffect, useMemo, useRef, useState } from 'react';
import { clarityEvent, gaEvent } from '../analytics';
import { formatPreOrderDate, getNextPreOrderDate } from '../utils/preOrderDate';
import styles from './InstagramStoryLanding.module.css';

const storySlides = {
  core: {
    id: 'core',
    eyebrow: 'The Core Challenge',
    title: 'You Can’t Do Everything',
    body: 'Every turn presents multiple strong options, but they do not lead to the same result. Decide what to do now, what can wait, and what you’re willing to give up.',
    image: '/images/webp/Card4-abstract_560w.webp',
    imageLarge: '/images/webp/Card4-abstract.webp',
    imageWidth: 560,
    imageLargeWidth: 1120,
    width: 1120,
    height: 752,
    alt: 'A figure at a crossroads facing resources, research, Wonders, and population.',
    cta: 'See the core challenge',
    destination: 'feature-core-challenge',
  },
  rivalry: {
    id: 'rivalry',
    eyebrow: 'A Different Kind of Conflict',
    title: 'Rivalry Without Combat',
    body: 'Players constantly read each other and compete for key opportunities. Taking something first can slow both of you down while another civilization pulls ahead.',
    image: '/images/webp/card5_Wonders_560w.webp',
    imageLarge: '/images/webp/card5_Wonders.webp',
    imageWidth: 560,
    imageLargeWidth: 1120,
    width: 1120,
    height: 752,
    alt: 'Wonders representing exclusive opportunities civilizations compete to claim.',
    cta: 'How rivalry works',
    destination: 'rivalry-without-combat',
  },
  strengths: {
    id: 'strengths',
    eyebrow: 'Asymmetric Civilizations',
    title: 'Different Strengths Shape Each Civilization',
    body: 'Each civilization pushes you toward certain strengths. The more you lean into one path, the more you fall behind elsewhere.',
    image: '/images/webp/Card1-TraitCharacters_560w.webp',
    imageLarge: '/images/webp/Card1-TraitCharacters.webp',
    imageWidth: 560,
    imageLargeWidth: 1120,
    width: 1120,
    height: 752,
    alt: 'Civilization leaders representing different strategic strengths.',
    cta: 'Explore civilizations',
    destination: 'feature-different-strengths',
  },
  components: {
    id: 'components',
    eyebrow: 'Inside the Box',
    title: 'Build Across Two Ages',
    body: 'A shared Technology Board, four City Boards, 400 Resource Cards, 14 Wonders, 88 Building Tiles, and the pieces that bring each civilization to life.',
    image: '/images/webp/Contents-6-Techboard.webp',
    width: 1200,
    height: 900,
    alt: 'The INCEPTUM Technology Board and game components arranged for play.',
    cta: 'See what’s in the box',
    destination: 'components',
  },
  preorder: {
    id: 'preorder',
    eyebrow: 'Waitlist-Only Offer',
    title: 'Join Before Preorders Open',
    body: `Waitlist members receive an exclusive preorder discount when the first print run opens on ${formatPreOrderDate(getNextPreOrderDate())}.`,
    image: '/images/webp/BoxFront_520w.webp',
    imageLarge: '/images/webp/BoxFront_1045x820.webp',
    imageWidth: 520,
    imageLargeWidth: 1045,
    width: 1045,
    height: 820,
    alt: 'INCEPTUM: Dawn of Civilizations board game box.',
    primaryCta: 'Get the waitlist offer',
    cta: 'Explore the full game',
    destination: 'about',
    final: true,
  },
};

const sequences = {
  e1: ['core', 'rivalry', 'strengths', 'components', 'preorder'],
  e2: ['components', 'strengths', 'core', 'rivalry', 'preorder'],
  e3: ['rivalry', 'core', 'strengths', 'components', 'preorder'],
};

export default function InstagramStoryLanding({ treatment, onWaitlistVisibilityChange }) {
  const sequence = sequences[treatment] || sequences.e1;
  const slides = useMemo(() => sequence.map((id) => storySlides[id]), [sequence]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [autoplayStopped, setAutoplayStopped] = useState(false);
  const [isVisible, setIsVisible] = useState(true);
  const stageRef = useRef(null);
  const touchStartX = useRef(0);
  const slide = slides[currentIndex];

  useEffect(() => {
    onWaitlistVisibilityChange?.(Boolean(slide.final));
    return () => onWaitlistVisibilityChange?.(false);
  }, [onWaitlistVisibilityChange, slide.final]);

  useEffect(() => {
    const stage = stageRef.current;
    if (!stage) return undefined;
    const observer = new IntersectionObserver(
      ([entry]) => setIsVisible(entry.isIntersecting),
      { threshold: 0.35 }
    );
    observer.observe(stage);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    gaEvent('ig_story_slide_view', {
      treatment,
      slide_id: slide.id,
      slide_position: currentIndex + 1,
    });
    clarityEvent(`ig_story_slide_${slide.id}`);
  }, [currentIndex, slide.id, treatment]);

  useEffect(() => {
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reducedMotion || autoplayStopped || !isVisible || slide.final) return undefined;
    const timer = window.setTimeout(() => setCurrentIndex((index) => index + 1), 5500);
    return () => window.clearTimeout(timer);
  }, [autoplayStopped, currentIndex, isVisible, slide.final]);

  const selectSlide = (index, action) => {
    setAutoplayStopped(true);
    setCurrentIndex(index);
    gaEvent('ig_story_interaction', { treatment, action, slide_id: slides[index].id });
    clarityEvent('ig_story_interaction');
  };

  const showPrevious = () => selectSlide(
    currentIndex === 0 ? slides.length - 1 : currentIndex - 1,
    'previous'
  );

  const showNext = () => selectSlide(
    currentIndex === slides.length - 1 ? 0 : currentIndex + 1,
    'next'
  );

  const showDetails = () => {
    setAutoplayStopped(true);
    gaEvent('ig_story_details_click', {
      treatment,
      slide_id: slide.id,
      destination: slide.destination,
    });
    clarityEvent('ig_story_details_click');
    document.getElementById(slide.destination)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const openWaitlist = () => {
    setAutoplayStopped(true);
    gaEvent('ig_story_waitlist_click', { treatment, slide_id: slide.id });
    clarityEvent('ig_story_waitlist_click');
    window.dispatchEvent(new Event('open-inceptum-waitlist'));
  };

  return (
    <section
      ref={stageRef}
      className={styles.stage}
      id="home"
      aria-roledescription="carousel"
      aria-label="Explore INCEPTUM"
      onPointerDown={() => setAutoplayStopped(true)}
      onFocusCapture={() => setAutoplayStopped(true)}
      onTouchStart={(event) => { touchStartX.current = event.touches[0].clientX; }}
      onTouchEnd={(event) => {
        const delta = event.changedTouches[0].clientX - touchStartX.current;
        if (Math.abs(delta) < 50) return;
        if (delta < 0) showNext(); else showPrevious();
      }}
    >
      <div className={styles.identity}>
        <span className={styles.gameName}>INCEPTUM</span>
        <span className={styles.gameSubtitle}>Dawn of Civilizations</span>
        <div className={styles.quickFacts} aria-label="Game details">
          <span>2–4 players</span><span>60–120 min</span><span>Ages 12+</span>
        </div>
      </div>

      <div className={styles.slide} aria-live="polite">
        <div className={styles.imageFrame}>
          <img
            key={slide.id}
            src={slide.imageLarge || slide.image}
            srcSet={slide.imageLarge
              ? `${slide.image} ${slide.imageWidth}w, ${slide.imageLarge} ${slide.imageLargeWidth}w`
              : undefined}
            sizes="(max-width: 768px) 100vw, 760px"
            alt={slide.alt}
            width={slide.width}
            height={slide.height}
            fetchPriority="high"
            className={`${styles.slideImage} ${slide.final ? styles.boxImage : ''}`}
          />
        </div>
        <div className={styles.slideCopy}>
          <span className={styles.eyebrow}>{slide.eyebrow}</span>
          <h1 className={styles.title}>{slide.title}</h1>
          <p className={styles.body}>{slide.body}</p>
          <div className={styles.actions}>
            {slide.final && (
              <button type="button" className={styles.primaryAction} onClick={openWaitlist}>
                {slide.primaryCta}
              </button>
            )}
            <button type="button" className={styles.detailsAction} onClick={showDetails}>
              {slide.cta}<span className={styles.downChevron} aria-hidden="true" />
            </button>
          </div>
        </div>
      </div>

      <div className={styles.controls}>
        <button type="button" className={styles.arrow} onClick={showPrevious} aria-label="Previous story">‹</button>
        <div className={styles.dots}>
          {slides.map((item, index) => (
            <button
              key={item.id}
              type="button"
              className={`${styles.dot} ${index === currentIndex ? styles.activeDot : ''}`}
              onClick={() => selectSlide(index, 'dot')}
              aria-label={`Show story ${index + 1}: ${item.title}`}
              aria-current={index === currentIndex ? 'true' : undefined}
            />
          ))}
        </div>
        <button type="button" className={styles.arrow} onClick={showNext} aria-label="Next story">›</button>
      </div>
    </section>
  );
}